import { DownloadableFileObject } from "@citychallenge/common";
import { DownloadState } from "./download-state";
import { DownloadTask } from "./download-task";
import { DefaultLogger } from "../logger";
import { FileService } from "../services/file";

/**
 * Create a download task
 * @param fileService Service that governs the downloading
 * @param file The object that needs to be downloads
 * @returns {DownloadTask}
 */
export function createDownloadTask(
  fileService: FileService,
  file: DownloadableFileObject
): DownloadTask {
  const path = fileService.resolveResourcesPath(file.path);

  const state = new DownloadState();

  const checkFile = async () => {
    try {
      const stat = await fileService.stat(path);
      state.setCompleted(+stat.size);
      return true;
    } catch {
      return false;
    } finally {
      state.setIsFileChecked(true);
    }
  };

  const downloadFile = async () => {
    if (await checkFile()) return;

    await fileService.mkdir(fileService.getDirectory(path));

    const download = fileService.downloadFile(file.originalFilePath, path, {
      begin: ({ contentLength }) => {
        state.setTotalBytes(contentLength);
      },
      progress: ({ bytesWritten, contentLength }) => {
        state.setWrittenAndTotalBytes(bytesWritten, contentLength);
      },
    });

    await download.promise;

    state.setCompleted();
  };

  const download = async () => {
    state.setError(undefined);
    try {
      await downloadFile();
    } catch (e) {
      DefaultLogger.capture(e);

      if (checkIfUnrecoverableError(e)) {
        // If error is unrecoverable we can just set the download complete,
        // the file will never be downloaded anyway.
        return state.setCompleted();
      }
      state.setError(e);
      throw e;
    }
  };

  return { state, checkFile, download };
}

function checkIfUnrecoverableError(e: any) {
  if (typeof e !== "object") return false;

  if (e.userInfo?.NSUnderlyingError?.code) {
    // iOS error. Check if error is unrecoverable, and return true if that is the case
    const errors = [
      "-1000", // Bad URL
      "-1002", // Unsupported URL
      "-1100", // File does not exist
    ];

    return errors.includes(e.userInfo.NSUnderlyingError.code);
  }

  return false;
}
